import { useEffect, useMemo, useState } from 'react';
import { countingAPI } from '../../API/countingAPI';
import { fmtDwell } from '../../Utils/format';

const toLocalDateStr = (d) => {
  if (!d) return '';
  if (typeof d === 'string' && d.length === 10 && /^\d{4}-\d{2}-\d{2}$/.test(d)) {
    return d;
  }
  const date = d instanceof Date ? d : new Date(d);
  if (isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** UTC-based date string — matches the UTC display used in VegaTrafficChart table */
const toUtcDateStr = (d) => {
  if (!d) return '';
  if (typeof d === 'string' && d.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(d)) {
    return d.substring(0, 10);
  }
  const date = d instanceof Date ? d : new Date(d);
  if (isNaN(date.getTime())) return '';
  return date.toISOString().substring(0, 10);
};

const getUtcTodayStr = () => new Date().toISOString().substring(0, 10);

const getUtcYesterdayStr = () => {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().substring(0, 10);
};

const padH = (h) => `${String(h).padStart(2, '0')}h`;

const useStoreStats = (
  clientId,
  storeId,
  startDate,
  endDate,
  period = 'today',
  granularity = 'HOUR',
  deviceIds = null,
) => {
  const [aggregates, setAggregates] = useState([]);
  const [chartAggregates, setChartAggregates] = useState([]);
  const [dailySummary, setDailySummary] = useState([]);
  const [dwellSummary, setDwellSummary] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Committed state matching currently loaded dataset
  const [committedPeriod, setCommittedPeriod] = useState(period);
  const [committedStartDate, setCommittedStartDate] = useState(startDate);
  const [committedEndDate, setCommittedEndDate] = useState(endDate);
  const [committedGranularity, setCommittedGranularity] = useState(granularity);

  const deviceIdsKey = useMemo(() => {
    if (!Array.isArray(deviceIds) || deviceIds.length === 0) return null;
    return deviceIds.slice().sort().join(',');
  }, [deviceIds]);

  // UTC-based date strings for chart/table filtering (matches UTC display in VegaTrafficChart)
  const utcTodayStr = useMemo(() => (startDate ? toUtcDateStr(startDate) : getUtcTodayStr()), [startDate]);
  const utcYesterdayStr = useMemo(() => {
    if (startDate) {
      const d = new Date(startDate);
      d.setUTCDate(d.getUTCDate() - 1);
      return toUtcDateStr(d);
    }
    return getUtcYesterdayStr();
  }, [startDate]);

  useEffect(() => {
    let isCancelled = false;
    if (!clientId || !startDate || !endDate) return;
    setIsLoading(true);
    const ids = deviceIdsKey ? deviceIdsKey.split(',') : null;
    const needChartFetch = granularity !== 'HOUR';
    const storeIdsFilter = storeId ? [storeId] : null;
    const aggStart = period === 'today'
      ? (() => { const d = new Date(startDate); d.setDate(d.getDate() - 1); d.setHours(0, 0, 0, 0); return d; })()
      : startDate;
    const dailyStart = (() => {
      const d1 = new Date(startDate);
      d1.setHours(0, 0, 0, 0);
      const d2 = new Date();
      d2.setDate(d2.getDate() - 14);
      d2.setHours(0, 0, 0, 0);
      return d1 < d2 ? d1 : d2;
    })();
    const calls = [
      countingAPI.getAggregates(clientId, aggStart, endDate, 'HOUR', storeId, ids),
      countingAPI.getAggregates(clientId, dailyStart, endDate, 'DAILY', storeId, ids),
      countingAPI.getDwellSummary(clientId, startDate, endDate, storeIdsFilter, 1, 1000, ids),
    ];
    if (needChartFetch) {
      calls.push(countingAPI.getAggregates(clientId, aggStart, endDate, granularity, storeId, ids));
    }
    Promise.all(calls).then(([aggRes, dailyRes, dwellRes, chartAggRes]) => {
      if (isCancelled) return;
      setAggregates(aggRes?.value ?? []);
      setChartAggregates(needChartFetch ? (chartAggRes?.value ?? []) : (aggRes?.value ?? []));
      const dailyItems = Array.isArray(dailyRes?.value)
        ? dailyRes.value
        : (dailyRes?.value?.items || dailyRes?.value?.Items || (Array.isArray(dailyRes) ? dailyRes : []));
      setDailySummary(Array.isArray(dailyItems) ? dailyItems : []);
      const dwellItems = dwellRes?.value?.items || dwellRes?.value?.Items || dwellRes?.value || (Array.isArray(dwellRes) ? dwellRes : []);
      setDwellSummary(Array.isArray(dwellItems) ? dwellItems : []);
      setCommittedPeriod(period);
      setCommittedStartDate(startDate);
      setCommittedEndDate(endDate);
      setCommittedGranularity(granularity);
      setIsLoading(false);
    }).catch(() => {
      if (!isCancelled) setIsLoading(false);
    });

    return () => {
      isCancelled = true;
    };
  }, [clientId, storeId, startDate, endDate, granularity, period, deviceIdsKey]);

  const todayAggs = useMemo(
    () => aggregates.filter(a => toUtcDateStr(a.period) === utcTodayStr),
    [aggregates, utcTodayStr],
  );
  const yesterdayAggs = useMemo(
    () => aggregates.filter(a => toUtcDateStr(a.period) === utcYesterdayStr),
    [aggregates, utcYesterdayStr],
  );

  const todayChartData = useMemo(
    () => todayAggs
      .sort((a, b) => new Date(a.period) - new Date(b.period))
      .map(a => ({ label: padH(new Date(a.period).getUTCHours()), v: a.totalInCount })),
    [todayAggs],
  );

  const yesterdayChartData = useMemo(
    () => yesterdayAggs
      .sort((a, b) => new Date(a.period) - new Date(b.period))
      .map(a => ({ label: padH(new Date(a.period).getUTCHours()), v: a.totalInCount })),
    [yesterdayAggs],
  );

  // Chart data in MetricSnapshot format for VegaTrafficChart
  const todayChartSource = useMemo(
    () => chartAggregates.filter(a => toUtcDateStr(a.period) === utcTodayStr),
    [chartAggregates, utcTodayStr],
  );
  const yesterdayChartSource = useMemo(
    () => chartAggregates.filter(a => toUtcDateStr(a.period) === utcYesterdayStr),
    [chartAggregates, utcYesterdayStr],
  );

  const boundedChartSource = useMemo(() => {
    if (committedPeriod === 'today') {
      return todayChartSource;
    }
    if (committedStartDate) {
      const startStr = toLocalDateStr(committedStartDate);
      return chartAggregates.filter(a => toLocalDateStr(a.period) >= startStr);
    }
    return chartAggregates;
  }, [committedPeriod, todayChartSource, chartAggregates, committedStartDate]);

  const visitorsToday = useMemo(
    () => todayAggs.reduce((s, a) => s + a.totalInCount, 0),
    [todayAggs],
  );
  const visitorsYesterday = useMemo(
    () => yesterdayAggs.reduce((s, a) => s + a.totalInCount, 0),
    [yesterdayAggs],
  );
  const todayTrend = visitorsYesterday > 0
    ? Math.round(((visitorsToday - visitorsYesterday) / visitorsYesterday) * 100)
    : 0;

  const currentPeriodDaily = useMemo(() => {
    if (!startDate || !endDate) return dailySummary;
    const sStr = toLocalDateStr(startDate);
    const eStr = toLocalDateStr(endDate);
    return dailySummary.filter(d => {
      const k = toLocalDateStr(d.period ?? d.Period ?? d.date ?? d.Date);
      return k >= sStr && k <= eStr;
    });
  }, [dailySummary, startDate, endDate]);

  const sparkline7d = useMemo(() => {
    const byDate = {};
    dailySummary.forEach(d => {
      const k = toLocalDateStr(d.period ?? d.Period ?? d.date ?? d.Date);
      byDate[k] = (byDate[k] || 0) + (d.totalInCount ?? d.TotalInCount ?? 0);
    });

    const result = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const localKey = toLocalDateStr(d);
      result.push({ v: byDate[localKey] || 0 });
    }
    return result;
  }, [dailySummary]);

  const periodSparkline = useMemo(() => {
    if (period === 'today') {
      const sorted = todayAggs
        .slice()
        .sort((a, b) => new Date(a.period) - new Date(b.period))
        .map(a => ({ v: a.totalInCount }));
      if (sorted.length === 1) {
        return [{ v: 0 }, sorted[0]];
      }
      return sorted;
    }

    const byDate = {};
    dailySummary.forEach(d => {
      const k = toLocalDateStr(d.period ?? d.Period ?? d.date ?? d.Date);
      byDate[k] = (byDate[k] || 0) + (d.totalInCount ?? d.TotalInCount ?? 0);
    });

    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setHours(0, 0, 0, 0);

    const diffDays = Math.round((end - start) / 86400000);
    if (diffDays <= 0) {
      const dayStr = toLocalDateStr(startDate);
      const dayAggs = aggregates
        .filter(a => toLocalDateStr(a.period) === dayStr)
        .slice()
        .sort((a, b) => new Date(a.period) - new Date(b.period))
        .map(a => ({ v: a.totalInCount }));
      if (dayAggs.length > 1) return dayAggs;
      if (dayAggs.length === 1) return [{ v: 0 }, dayAggs[0]];
      return [{ v: 0 }, { v: byDate[dayStr] || 0 }];
    }

    const result = [];
    const curr = new Date(start);
    while (curr <= end) {
      const localKey = toLocalDateStr(curr);
      result.push({ v: byDate[localKey] || 0 });
      curr.setDate(curr.getDate() + 1);
    }
    return result;
  }, [period, todayAggs, dailySummary, startDate, endDate, aggregates]);

  const periodAggs = useMemo(
    () => (period === 'today' ? todayAggs : aggregates),
    [period, todayAggs, aggregates],
  );

  const last7Days = useMemo(
    () => sparkline7d.reduce((s, d) => s + d.v, 0),
    [sparkline7d],
  );

  const visitorsTotal = useMemo(
    () => currentPeriodDaily.reduce((s, d) => s + (d.totalInCount ?? d.TotalInCount ?? 0), 0),
    [currentPeriodDaily],
  );

  const prev7Total = useMemo(() => {
    const byDate = {};
    dailySummary.forEach(d => {
      const k = toLocalDateStr(d.period ?? d.Period ?? d.date ?? d.Date);
      byDate[k] = (byDate[k] || 0) + (d.totalInCount ?? d.TotalInCount ?? 0);
    });
    let sum = 0;
    for (let i = 13; i >= 7; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const localKey = toLocalDateStr(d);
      sum += (byDate[localKey] || 0);
    }
    return sum;
  }, [dailySummary]);

  const weekTrend = prev7Total > 0
    ? Math.round(((last7Days - prev7Total) / prev7Total) * 100)
    : 0;

  const peakAgg = useMemo(
    () => periodAggs.reduce((max, a) => ((!max || a.totalInCount > max.totalInCount) ? a : max), null),
    [periodAggs],
  );
  const peakHour = peakAgg ? `${String(new Date(peakAgg.period).getHours()).padStart(2, '0')}:00` : '--:--';
  const peakVisitors = peakAgg ? peakAgg.totalInCount : 0;
  const periodDwellSummary = useMemo(() => {
    if (!dwellSummary || !dwellSummary.length) return [];
    if (period === 'today') {
      return dwellSummary.filter(r => toUtcDateStr(r.date ?? r.Date) === utcTodayStr);
    }
    return dwellSummary;
  }, [dwellSummary, period, utcTodayStr]);

  const avgDwellSeconds = useMemo(() => {
    if (periodDwellSummary && periodDwellSummary.length > 0) {
      const withDwell = periodDwellSummary.filter(r => ((r.visitorCount ?? r.VisitorCount) || 0) > 0 && ((r.avgDwellSeconds ?? r.AvgDwellSeconds) || 0) > 0);
      if (withDwell.length > 0) {
        const totalVis = withDwell.reduce((s, r) => s + ((r.visitorCount ?? r.VisitorCount) || 0), 0);
        if (totalVis > 0) {
          const totalDwellProd = withDwell.reduce((s, r) => s + (((r.avgDwellSeconds ?? r.AvgDwellSeconds) || 0) * ((r.visitorCount ?? r.VisitorCount) || 0)), 0);
          return Math.round(totalDwellProd / totalVis);
        }
      }
      const simpleDwell = periodDwellSummary.filter(r => ((r.avgDwellSeconds ?? r.AvgDwellSeconds) || 0) > 0);
      if (simpleDwell.length > 0) {
        const sum = simpleDwell.reduce((s, r) => s + ((r.avgDwellSeconds ?? r.AvgDwellSeconds) || 0), 0);
        return Math.round(sum / simpleDwell.length);
      }
    }
    const withDwell = periodAggs.filter(a => (a.avgDwellSeconds || 0) > 0 && (a.totalInCount || 0) > 0);
    if (!withDwell.length) return 0;
    const totalVis = withDwell.reduce((s, a) => s + (a.totalInCount || 0), 0);
    if (!totalVis) return 0;
    const totalDwellProd = withDwell.reduce((s, a) => s + ((a.avgDwellSeconds || 0) * (a.totalInCount || 0)), 0);
    return Math.round(totalDwellProd / totalVis);
  }, [periodDwellSummary, periodAggs]);

  const dailyChartData = useMemo(() => {
    const byDate = {};
    dailySummary.forEach(d => {
      const k = toLocalDateStr(d.period ?? d.Period ?? d.date ?? d.Date);
      if (k) {
        byDate[k] = (byDate[k] || 0) + (d.totalInCount ?? d.TotalInCount ?? 0);
      }
    });
    return Object.entries(byDate)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([label, v]) => ({ label: label.substring(5), v }));
  }, [dailySummary]);

  const genderSummary = useMemo(() => ({
    maleCount: periodAggs.reduce((s, a) => s + (a.totalMaleCount || 0), 0),
    femaleCount: periodAggs.reduce((s, a) => s + (a.totalFemaleCount || 0), 0),
    unclassifiedCount: 0,
  }), [periodAggs]);

  const ageSummary = useMemo(() => {
    const child = periodAggs.reduce((s, a) => s + (a.totalChildCount || 0), 0);
    const adult = periodAggs.reduce((s, a) => s + (a.totalAdultCount || 0), 0);
    const elder = periodAggs.reduce((s, a) => s + (a.totalElderCount || 0), 0);
    return {
      childCount: child,
      adultCount: adult,
      elderCount: elder,
      total: child + adult + elder,
    };
  }, [periodAggs]);

  return {
    isLoading,
    visitorsToday,
    visitorsTotal,
    todayTrend,
    last7Days,
    weekTrend,
    sparkline7d,
    periodSparkline,
    peakHour,
    peakVisitors,
    dwellFormatted: fmtDwell(avgDwellSeconds),
    genderSummary,
    ageSummary,
    todayChartData,
    yesterdayChartData,
    dailyChartData,
    // MetricSnapshot format for VegaTrafficChart
    todayChartSource,
    yesterdayChartSource,
    chartSource: boundedChartSource,
    chartData: boundedChartSource,
    compareData: committedPeriod === 'today' ? yesterdayChartSource : [],
    committedPeriod,
    committedStartDate,
    committedEndDate,
    committedGranularity,
    committedIsToday: committedPeriod === 'today',
  };
};

export default useStoreStats;
